import { api } from './axios';

/**
 * API для работы с опросами в групповых чатах
 * Базовый путь: /api/chats/{chatId}/polls
 * Все эндпоинты требуют Bearer Token и доступ только участникам чата.
 */

/**
 * Валидация ID
 * @param {string|number} id - Идентификатор
 * @param {string} name - Название поля для ошибки
 */
const validateId = (id, name = 'ID') => {
    if (!id && id !== 0) throw new Error(`${name} is required`);
};

export const pollsApi = {
    /**
     * Создать опрос в чате
     * POST /api/chats/{chatId}/polls
     * @param {string} chatId - ID чата (UUID)
     * @param {Object} pollData - Данные опроса (PollRequest)
     * @param {string} pollData.question - Текст вопроса (1–500 символов)
     * @param {boolean} [pollData.multipleChoice=false] - Множественный выбор
     * @param {Array<{text: string}>} pollData.options - Варианты ответов (2–20 элементов)
     * @param {string} [pollData.messageId] - ID сообщения для привязки (UUID)
     * @returns {Promise<Object>} Созданный опрос (PollResponse)
     */
    create: async (chatId, pollData) => {
        validateId(chatId, 'chatId');
        if (!pollData) throw new Error('Poll data is required');
        if (!pollData.question || !pollData.question.trim()) {
            throw new Error('question is required');
        }
        if (!Array.isArray(pollData.options) || pollData.options.length < 2) {
            throw new Error('At least 2 options are required');
        }
        try {
            const body = {
                question: pollData.question.trim(),
                multipleChoice: pollData.multipleChoice ?? false,
                options: pollData.options.map(opt => ({ text: opt.text.trim() })),
            };
            if (pollData.messageId) {
                body.messageId = pollData.messageId;
            }
            const { data } = await api.post(`/chats/${chatId}/polls`, body);
            return data;
        } catch (error) {
            console.error(`[pollsApi.create] Failed to create poll in chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить все опросы чата
     * GET /api/chats/{chatId}/polls
     * @param {string} chatId - ID чата (UUID)
     * @returns {Promise<Array>} Список опросов (PollResponse[])
     */
    getAll: async (chatId) => {
        validateId(chatId, 'chatId');
        try {
            const { data } = await api.get(`/chats/${chatId}/polls`);
            return Array.isArray(data) ? data : [];
        } catch (error) {
            console.error(`[pollsApi.getAll] Failed to fetch polls for chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить опрос по ID
     * GET /api/chats/{chatId}/polls/{pollId}
     * @param {string} chatId - ID чата (UUID)
     * @param {string} pollId - ID опроса (UUID)
     * @returns {Promise<Object>} Опрос (PollResponse)
     */
    getById: async (chatId, pollId) => {
        validateId(chatId, 'chatId');
        validateId(pollId, 'pollId');
        try {
            const { data } = await api.get(`/chats/${chatId}/polls/${pollId}`);
            return data;
        } catch (error) {
            console.error(`[pollsApi.getById] Failed to fetch poll ${pollId} in chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Проголосовать в опросе
     * POST /api/chats/{chatId}/polls/{pollId}/vote
     * При повторном голосовании старые голоса удаляются, новые сохраняются.
     * @param {string} chatId - ID чата (UUID)
     * @param {string} pollId - ID опроса (UUID)
     * @param {string[]} optionIds - Массив ID вариантов (UUID)
     * @returns {Promise<Object>} Результат голосования (PollVoteResponse)
     */
    vote: async (chatId, pollId, optionIds) => {
        validateId(chatId, 'chatId');
        validateId(pollId, 'pollId');
        if (!Array.isArray(optionIds) || optionIds.length === 0) {
            throw new Error('optionIds must be a non-empty array');
        }
        try {
            const { data } = await api.post(`/chats/${chatId}/polls/${pollId}/vote`, { optionIds });
            return data;
        } catch (error) {
            console.error(`[pollsApi.vote] Failed to vote in poll ${pollId}:`, error.message);
            throw error;
        }
    },

    /**
     * Отменить голос в опросе
     * DELETE /api/chats/{chatId}/polls/{pollId}/vote
     * Удаляет все голоса текущего пользователя в указанном опросе.
     * @param {string} chatId - ID чата (UUID)
     * @param {string} pollId - ID опроса (UUID)
     * @returns {Promise<void>}
     */
    removeVote: async (chatId, pollId) => {
        validateId(chatId, 'chatId');
        validateId(pollId, 'pollId');
        try {
            await api.delete(`/chats/${chatId}/polls/${pollId}/vote`);
        } catch (error) {
            console.error(`[pollsApi.removeVote] Failed to remove vote in poll ${pollId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить свой голос в опросе
     * GET /api/chats/{chatId}/polls/{pollId}/my-vote
     * @param {string} chatId - ID чата (UUID)
     * @param {string} pollId - ID опроса (UUID)
     * @returns {Promise<Object>} Результат (PollVoteResponse) с selectedOptionIds
     */
    getMyVote: async (chatId, pollId) => {
        validateId(chatId, 'chatId');
        validateId(pollId, 'pollId');
        try {
            const { data } = await api.get(`/chats/${chatId}/polls/${pollId}/my-vote`);
            return data;
        } catch (error) {
            console.error(`[pollsApi.getMyVote] Failed to fetch my vote in poll ${pollId}:`, error.message);
            throw error;
        }
    },

    /**
     * Закрыть опрос
     * PUT /api/chats/{chatId}/polls/{pollId}/close
     * После закрытия голосование становится невозможным.
     * Закрыть опрос может любой участник чата.
     * @param {string} chatId - ID чата (UUID)
     * @param {string} pollId - ID опроса (UUID)
     * @returns {Promise<Object>} Обновлённый опрос (PollResponse)
     */
    close: async (chatId, pollId) => {
        validateId(chatId, 'chatId');
        validateId(pollId, 'pollId');
        try {
            const { data } = await api.put(`/chats/${chatId}/polls/${pollId}/close`);
            return data;
        } catch (error) {
            console.error(`[pollsApi.close] Failed to close poll ${pollId}:`, error.message);
            throw error;
        }
    },
};

export default pollsApi;
